import React from 'react';
import ReactDOM from 'react-dom/client';
import { App } from '@/ui/App';
import { createGame } from '@/game/Game';
import { installEventBusBridge } from '@/ui/store/eventBusBridge';
import { installAutoSaveBridge } from '@/save/AutoSaveBridge';
import { useGameStore } from '@/ui/store/gameStore';
import { saveManager } from '@/save';
import { authApi, configApi, rewardsApi, ApiError } from '@/net/api';
import {
  loadGuestCreds,
  saveGuestCreds,
  clearGuestCreds,
  makeGuestPassword,
} from '@/net/guestSession';
import { ErrorBoundary } from '@/ui/components/ErrorBoundary';
import { refreshClaimableAchievements } from '@/ui/avatarRewards';
import { equippedCap } from '@/config/balance.config';
import { BIOMES_BY_ID } from '@/config/biomes.config';
import { getEventByBiomeId, isEventActive } from '@/config/events.config';
import './index.css';

/**
 * Główny punkt wejścia aplikacji (async).
 *
 * Tryb gościa: bootstrap rozwiązuje sesję AUTOMATYCZNIE — brak ekranu
 * logowania. `resolveSession()` próbuje: cookie → re-login zapisanego gościa
 * → utworzenie nowego konta-gościa. Po nim gracz jest zawsze zalogowany.
 *
 *  0. `resolveSession()` — cookie / re-login gościa / nowe konto-gość.
 *  1. Mount React App (auth już ustawione → App od razu LoadingScreen).
 *  2. `saveManager.load()` — ServerSaveAdapter: GET /saves/me + ew. import IDB.
 *  3. Hydratacja Zustand store ze stanu save'a.
 *  4. `setSaveLoaded(true)` — App.tsx przechodzi z LoadingScreen do onboardingu.
 *  5. Bridge'y (EventBus + AutoSave).
 *  6. `waitForOnboardingCompletion()` — avatar + starter pety.
 *  7. `createGame()` — Phaser startuje z prawidłowym save.equipped.
 *
 * Race-safe order:
 *   - resolveSession PRZED renderem → App nie startuje bez auth (auto-guest gwarantowany)
 *   - save load PO auth → ServerSaveAdapter ma ważne cookie
 *   - setSaveLoaded po hydratacji → App.tsx nie mignie AvatarSelectorem
 *     z placeholder store state
 *   - waitForOnboarding przed createGame → Phaser dostaje gotowe save.equipped
 */

// React root — trzymany modułowo, żeby `showBootstrapError` mógł zrobić
// `unmount()` przed ręcznym czyszczeniem kontenera (Codex 4.F.3 LOW 1).
let reactRoot: ReactDOM.Root | null = null;

// Po deployu otwarta karta może mieć stary entrypoint wskazujący lazy chunk,
// którego nie ma już w nowym kontenerze. Vite emituje wtedy ten event przed
// odrzuceniem importu. Jeden reload pobiera świeży index.html i aktualne hashe.
// Cooldown zapobiega pętli, gdy prawdziwą przyczyną jest brak sieci/CDN.
const CHUNK_RELOAD_KEY = 'capibara:chunk-reload-at';
const CHUNK_RELOAD_COOLDOWN_MS = 60_000;

window.addEventListener('vite:preloadError', (event) => {
  // Gdy telefon jest jawnie offline, reload tylko wyrzuci gracza z już
  // działającej gry na stronę offline przeglądarki. Nie anulujemy błędu:
  // lokalny boundary modala pozwoli zamknąć ekran i grać dalej.
  if (window.navigator.onLine === false) {
    console.error('[chunks] Brak sieci — pomijam automatyczny reload.');
    return;
  }

  const now = Date.now();
  let lastReloadAt = 0;
  try {
    lastReloadAt = Number(window.sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);
  } catch {
    // Bez trwałego markera nie da się zagwarantować braku reload-loop. Nie
    // anulujemy eventu: błąd przejmie istniejący ErrorBoundary z przyciskiem.
    console.error('[chunks] Brak sessionStorage — pomijam automatyczny reload.');
    return;
  }

  if (now - lastReloadAt < CHUNK_RELOAD_COOLDOWN_MS) {
    console.error('[chunks] Lazy chunk nadal niedostępny po odświeżeniu.');
    return;
  }

  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
  } catch {
    console.error('[chunks] Nie udało się zapisać blokady reloadu.');
    return;
  }
  event.preventDefault();
  window.location.reload();
});

async function bootstrap(): Promise<void> {
  const gameRoot = document.getElementById('game');
  const uiRoot = document.getElementById('ui');

  if (!gameRoot || !uiRoot) {
    throw new Error('Brak wymaganych elementów #game lub #ui w index.html');
  }

  // 0. Rozwiąż sesję — tryb gościa, bez ekranu logowania. Rzut (sieć) →
  //    propaguje do `showBootstrapError` („Brak połączenia").
  await resolveSession();

  // 0b. Configi z serwera (Etap 5, 5.0.0b + BANK-0). Single source of truth
  //     dla UI — komponenty czytają z `useGameStore.tradingConfig`/`bankConfig`
  //     zamiast z `balance.config.ts`. Rzut (sieć/5xx) → showBootstrapError.
  //     Endpointy są PUBLICZNE (gość też dostaje), więc kolejność z auth
  //     nie ma znaczenia, ale trzymamy po auth żeby `request()` miał cookie
  //     do ewentualnego logu po stronie serwera. Promise.all — dwa fetche
  //     równolegle, bank nie dokłada RTT do czasu startu.
  const [tradingCfg, bankCfg] = await Promise.all([
    configApi.getTradingPlaza(),
    configApi.getBank(),
  ]);
  useGameStore.getState().setTradingConfig(tradingCfg.config, tradingCfg.version);
  useGameStore.getState().setBankConfig(bankCfg.config, bankCfg.version);

  // 1. React mount PO resolveSession — auth już ustawione, App od razu
  //    LoadingScreen (gate `!saveLoaded`).
  reactRoot = ReactDOM.createRoot(uiRoot);
  reactRoot.render(
    <React.StrictMode>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </React.StrictMode>,
  );

  // 1b. (Codex splash MED 1 fix) Usuń statyczny #boot-splash z index.html.
  //     React LoadingScreen + SplashBackground przejmują od tego momentu —
  //     identyczny look, więc gracz nie widzi przejścia. Fade-out 400 ms
  //     żeby uniknąć abrupt flash gdyby były minimalne różnice rendering.
  removeBootSplash();

  // 2. Save load — ServerSaveAdapter (sesja 34B). GET /saves/me; konto bez
  //    zainicjalizowanego save → import lokalnego IDB albo fresh.
  let save = await saveManager.load();

  // 2a. Self-heal: jeśli currentBiomeId istnieje w configu ale brak go w
  // biomesUnlocked — dopisz. Wynika z sesji 26 dev switcha który zmieniał
  // currentBiomeId bez aktualizacji biomesUnlocked. Bez tej migracji gracze
  // z sesji 26 widzieliby aktualny biom jako "🔒 Zamknięty" w mapie
  // (Codex sesja 27 review, non-blocker #2).
  const currentBiome = BIOMES_BY_ID[save.currentBiomeId];
  const currentEvent = getEventByBiomeId(save.currentBiomeId);
  const currentBiomeIsAvailable = currentBiome?.enabled === true
    || (currentEvent !== undefined && isEventActive(currentEvent.id));
  if (
    save.currentBiomeId
    && currentBiome
    && currentBiomeIsAvailable
    && !save.biomesUnlocked.includes(save.currentBiomeId)
  ) {
    await saveManager.update((current) => ({
      ...current,
      biomesUnlocked: [...current.biomesUnlocked, current.currentBiomeId],
    }));
    save = saveManager.getCurrent();
    console.info(
      `[bootstrap] Self-heal: dopisano '${save.currentBiomeId}' do biomesUnlocked.`,
    );
  }

  // 3. Hydratacja Zustand store ze stanu save'a.
  //
  // Sesja 30: onboarding 3-fazowy (avatar → starter pets → tutorial). Każda
  // faza ma swój flag w `save.flags.*`. Save migration v1→v2 dba o backward
  // compat (gracze z progresem dostają `starterPetsChosen=true` automatycznie).
  //
  // `isFirstRun` (legacy) = `!avatarSelected` — pierwszy ekran onboardingu.
  // App.tsx czyta `avatarSelected`/`starterPetsChosen` żeby wybrać który modal
  // renderować.
  const avatarSelected = save.flags.avatarSelected === true;
  const starterPetsChosen = save.flags.starterPetsChosen === true;
  const hasSeenFabTutorial = save.flags.hasSeenFabTutorial === true;

  useGameStore.setState({
    coins: save.currencies.coins,
    // Etap 5, 5.0.A — gems hydratowane z save.currencies (już w schemacie
    // PlayerSave.schema.ts od dawna jako forward-compat). Backend serwuje
    // przez `/saves/me` (analogicznie do coins). HUD pokazuje 3. ikonę.
    gems: save.currencies.gems,
    isFirstRun: !avatarSelected,
    avatarSelected,
    starterPetsChosen,
    hasSeenFabTutorial,
    avatarSkinId: save.profile.avatar.skinId,
    // Odblokowane skiny (selektor „Wygląd"). `?? [skin bieżący]` dla starych save'ów.
    unlockedSkins: save.profile.unlockedSkins ?? [save.profile.avatar.skinId],
    eggInventory: save.eggs,
    petInventory: save.inventory,
    equippedUids: save.equipped,
    upgrades: save.upgrades,
    currentBiomeId: save.currentBiomeId,
    // Mikstury — plecak + aktywne buffy (server-authoritative). `?? {}` na wypadek
    // bardzo starego save'a, który przeszedł migrację bez tych pól.
    potions: save.potions ?? {},
    activeEffects: save.activeEffects ?? {},
    // EV-3: stan eventów (album odkrycia/claim) — reflektor save.events.
    events: save.events ?? {},
    // power liczy się dynamicznie z equipped petów w WorldScene
  });

  // 3a. Backward-compat migration: auto-equip dla starych save'ów.
  //
  // Stare save'y (z sesji 1–21) mają `equipped: []`, bo wcześniej WorldScene
  // spawnował startera przez `spawnStarterPets()` (hardcoded Króliczek), z
  // ominięciem `save.equipped`. Po sesji 22 WorldScene czyta wyłącznie z
  // `save.equipped` — bez migracji gracz z pustym equipped widzi 0 petów
  // mimo że ma je w inventory → nie może niszczyć → stuck.
  //
  // Reguła: jeśli inventory != [] AND equipped == [], to bierzemy pierwsze
  // `equippedCap(pet_slots)` petów z inventory i wrzucamy do equipped.
  // Wykona się TYLKO raz — po `saveManager.update` save.equipped już nie
  // będzie puste. Recipe atomically updates + serializuje przez updateLock.
  if (save.equipped.length === 0 && save.inventory.length > 0) {
    const petSlotsLevel = save.upgrades['pet_slots'] ?? 0;
    const cap = equippedCap(petSlotsLevel);
    const toEquip = save.inventory.slice(0, cap).map((p) => p.uid);
    await saveManager.update((current) => ({
      ...current,
      equipped: toEquip,
    }));
    useGameStore.setState({ equippedUids: saveManager.getCurrent().equipped });
    console.info(
      `[bootstrap] Auto-equipped ${toEquip.length}/${cap} pets from inventory (old save migration)`,
    );
  }

  // 4. (Sesja 34B) Save załadowany i store zhydratowany → App.tsx przechodzi
  //    z LoadingScreen do onboardingu (avatar/starter) lub HUD.
  useGameStore.getState().setSaveLoaded(true);

  // 5. Bridge'y (oba PRZED Phaser, ale OK gdy gra nie startuje od razu).
  installEventBusBridge();
  installAutoSaveBridge();

  // 6. Czekaj aż onboarding ZAKOŃCZONY (avatar + starter pety wybrane).
  //    Sesja 30: WorldScene czyta `save.equipped` przy starcie → bez starter
  //    petów świat ma 0 entities. Phaser MUSI poczekać aż gracz przejdzie
  //    przez avatar + starter selector. `hasSeenFabTutorial` NIE jest częścią
  //    gate'a (gracz może startować bez zobaczenia tutorial — tutorial wskaże
  //    FAB później, gdy HUD jest visible).
  if (!isOnboardingComplete()) {
    await waitForOnboardingCompletion();
  }

  // 7. Phaser — startuje z aktualnym save.profile.avatar.skinId
  const game = createGame(gameRoot);

  // 7b. (Etap 5, 5.0.C) Auto-open DailyRewardModal jeśli claim dostępny.
  //     PO onboarding (gracz musi być w HUD, nie w avatar selectorze) +
  //     po `createGame` (Phaser już chodzi). Fire-and-forget — nie blokuje
  //     bootstrapu jeśli endpoint padnie (gracz po prostu nie zobaczy popup,
  //     może otworzyć ręcznie z FAB w przyszłości).
  void rewardsApi
    .getDailyStatus()
    .then((status) => {
      if (status.can_claim) {
        useGameStore.getState().setOpenModal('daily');
      }
    })
    .catch((err) => {
      console.warn('[main] daily-status fetch failed (non-blocking):', err);
    });

  // 7c. Nagrody „Pełny album" do odebrania → czerwona kropka w menu FAB.
  //     Łapie też graczy, którzy skompletowali biom PRZED wprowadzeniem nagród
  //     (catch-up). Fire-and-forget; gość nie ma albumu (bank read-only), więc
  //     request dla niego byłby pusty — pomijamy.
  if (useGameStore.getState().currentUser?.is_guest !== true) {
    void refreshClaimableAchievements();
  }

  // 8. Visibility pause (oszczędza baterię na mobile)
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      game.scene.pause('WorldScene');
    } else {
      game.scene.resume('WorldScene');
    }
  });

  // 9. Debug: wystaw na window w DEV + zainstaluj __dev helpery
  if (import.meta.env.DEV) {
    const w = window as unknown as { __game: Phaser.Game; __save: typeof saveManager };
    w.__game = game;
    w.__save = saveManager;
    // Dynamic import — w production build ten chunk nie jest emitowany
    // (tree-shake przez `import.meta.env.DEV` static check).
    const { installDevHelpers } = await import('./dev/devHelpers');
    installDevHelpers();
  }
}

/**
 * Rozwiązuje sesję przed renderem — TRYB GOŚCIA, bez ekranu logowania:
 *   1. Probe `/auth/me` — ważne cookie? → użyj sesji.
 *   2. Brak → zapisany gość w localStorage? → re-login (`/auth/login`).
 *   3. Brak → utwórz nowe konto-gość (`/auth/guest`) + zapisz creds.
 *
 * Po sukcesie `setAuth(true, user)`. Błąd sieci → rzut → `showBootstrapError`.
 *
 * Konto-gość nieaktualne (401 przy re-loginie — np. baza wyczyszczona) →
 * czyścimy creds i tworzymy nowe. Błąd sieci/500 przy re-loginie → NIE
 * kasujemy creds (konto istnieje, to przejściowa awaria) i rzucamy.
 */
async function resolveSession(): Promise<void> {
  // 1. Probe istniejącej sesji (cookie). 5s deadline — slow network nie
  //    blokuje startu w nieskończoność (Codex MED 1 sesji 33).
  const probeAbort = new AbortController();
  const probeTimeoutId = setTimeout(() => probeAbort.abort(), 5000);
  try {
    const user = await authApi.me({ signal: probeAbort.signal });
    if (user) {
      useGameStore.getState().setAuth(true, {
        id: user.id,
        username: user.username,
        is_guest: user.is_guest,
        display_name: user.display_name ?? user.username,
      });
      console.info(`[bootstrap] Sesja z cookie: ${user.username}`);
      return;
    }
  } catch (err) {
    console.warn('[bootstrap] /auth/me probe nieudany — tryb gościa:', err);
  } finally {
    clearTimeout(probeTimeoutId);
  }

  // 2. Brak sesji — re-login zapisanego konta-gościa.
  const stored = loadGuestCreds();
  if (stored) {
    try {
      const user = await authApi.login({
        username: stored.username,
        password: stored.password,
      });
      useGameStore.getState().setAuth(true, {
        id: user.id,
        username: user.username,
        is_guest: user.is_guest,
        display_name: user.display_name ?? user.username,
      });
      console.info('[bootstrap] Re-login gościa OK');
      return;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // Konto-gość nieaktualne (np. baza wyczyszczona) — wyczyść, utwórz nowe.
        console.warn('[bootstrap] Zapisane konto-gość nieaktualne — tworzę nowe');
        clearGuestCreds();
      } else {
        // Sieć / 500 — konto istnieje, to przejściowa awaria. NIE kasuj
        // creds. Rzuć → showBootstrapError („Brak połączenia").
        throw err;
      }
    }
  }

  // 3. Utwórz nowe konto-gość. Rzut (sieć) → showBootstrapError.
  const password = makeGuestPassword();
  const user = await authApi.createGuest(password);
  saveGuestCreds({ username: user.username, password });
  useGameStore.getState().setAuth(true, {
    id: user.id,
    username: user.username,
    is_guest: user.is_guest,
    display_name: user.display_name ?? user.username,
  });
  console.info(`[bootstrap] Nowe konto-gość: ${user.username}`);
}

/**
 * Sprawdza czy onboarding ukończony — gracz wybrał avatar + 2 startery.
 * `hasSeenFabTutorial` NIE jest gate'em (Phaser może startować bez tutoriala).
 */
function isOnboardingComplete(): boolean {
  const state = useGameStore.getState();
  return state.avatarSelected && state.starterPetsChosen;
}

/**
 * Czeka aż `avatarSelected && starterPetsChosen` — oba fazy onboardingu.
 * Resolve'uje natychmiast jeśli już skończony.
 *
 * Sesja 30: zastąpiło `waitForFirstRunCompletion` (które patrzyło tylko na
 * `isFirstRun`). Nowy gate dba żeby Phaser startował z prawidłowymi petami
 * w `save.equipped` — bez tego WorldScene rzeczy by 0 petów spawn'owało.
 */
function waitForOnboardingCompletion(): Promise<void> {
  return new Promise((resolve) => {
    if (isOnboardingComplete()) {
      resolve();
      return;
    }
    const unsubscribe = useGameStore.subscribe((state) => {
      if (state.avatarSelected && state.starterPetsChosen) {
        unsubscribe();
        resolve();
      }
    });
  });
}

/**
 * Usuwa statyczny #boot-splash z index.html z fade-out.
 *
 * Wołane po `reactRoot.render()` (płynne przejście do React'owego
 * LoadingScreen + SplashBackground) oraz w `showBootstrapError` (instant,
 * żeby error screen nie był zakryty przez splash).
 *
 * Idempotentne — multiple wywołań to no-op (po fade-out element jest
 * usuwany z DOM).
 */
function removeBootSplash(immediate: boolean = false): void {
  const splash = document.getElementById('boot-splash');
  if (!splash) return;
  if (immediate) {
    splash.remove();
    return;
  }
  splash.classList.add('hide');
  setTimeout(() => splash.remove(), 500);
}

/**
 * Ekran krytycznego błędu startu (sesja 4.F.3). Bootstrap pada najczęściej gdy
 * API jest nieosiągalne podczas `saveManager.load()` — wtedy propaguje
 * `ApiError(code:'network_error')`. Pokazujemy przyjazny komunikat „brak
 * połączenia" + przycisk odświeżenia zamiast surowego błędu.
 *
 * Czysty DOM (nie React) — błąd mógł dotyczyć samego Reacta; inline style
 * (nie Tailwind classes) — niezależne od stanu CSS. `textContent`, NIE
 * `innerHTML` (Codex 34B LOW 1 — komunikat może zawierać znaki HTML).
 */
function showBootstrapError(err: unknown): void {
  console.error('[bootstrap] Krytyczny błąd przy starcie:', err);

  // (Codex splash MED 1 fix) Usuń boot-splash instant żeby error screen
  // nie był zakryty przez splash + spinner. Bez tego gracz widziałby splash
  // z napisem „Ładowanie gry…" pod error overlay'em.
  removeBootSplash(true);

  const ui = document.getElementById('ui');
  if (!ui) return;

  const isNetwork = err instanceof ApiError && err.code === 'network_error';
  const detail = (err as Error)?.message ?? String(err);

  // Unmount React root przed ręcznym czyszczeniem kontenera — daje Reactowi
  // szansę na cleanup efektów/subskrypcji (Codex 4.F.3 LOW 1).
  reactRoot?.unmount();
  reactRoot = null;
  ui.replaceChildren();

  // `pointer-events:auto` na wrapperze (Codex splash FIXES MED 1):
  // `#ui` ma `pointer-events:none` (HUD overlay nad Phaser canvas — puste
  // obszary są przezroczyste dla kliknięć). Normalny React flow przywraca
  // przez `pointer-events-auto` na onboarding rootcie (App.tsx). DOM-only
  // fallback tu musi to zrobić sam, inaczej przycisk „Spróbuj ponownie"
  // dziedziczy `none` i klik nie działa.
  const wrap = document.createElement('div');
  wrap.style.cssText =
    'position:fixed;inset:0;display:flex;align-items:center;justify-content:center;' +
    'background:#A8E6A1;font-family:sans-serif;padding:16px;pointer-events:auto;';

  const card = document.createElement('div');
  card.style.cssText =
    'background:#fff;border-radius:24px;padding:24px;max-width:340px;width:100%;' +
    'text-align:center;box-shadow:0 10px 40px rgba(0,0,0,0.2);';

  const h = document.createElement('h2');
  h.style.cssText = 'font-size:22px;font-weight:bold;color:#2f6b2a;margin:0 0 8px;';
  h.textContent = isNetwork ? 'Brak połączenia' : 'Coś poszło nie tak';

  const p = document.createElement('p');
  p.style.cssText = 'font-size:14px;color:#555;margin:0 0 20px;';
  p.textContent = isNetwork
    ? 'Nie można połączyć się z serwerem gry. Sprawdź internet i spróbuj ponownie.'
    : 'Gra nie mogła się uruchomić. Spróbuj ponownie za chwilę.';

  const btn = document.createElement('button');
  btn.style.cssText =
    'width:100%;background:#4caf50;color:#fff;font-weight:bold;font-size:16px;' +
    'border:none;border-radius:16px;padding:12px;cursor:pointer;';
  btn.textContent = 'Spróbuj ponownie';
  btn.addEventListener('click', () => window.location.reload());

  // Szczegół techniczny — drobny, do debugowania.
  const small = document.createElement('p');
  small.style.cssText = 'font-size:11px;color:#bbb;margin:14px 0 0;word-break:break-word;';
  small.textContent = detail;

  card.append(h, p, btn, small);
  wrap.appendChild(card);
  ui.appendChild(wrap);
}

bootstrap().catch(showBootstrapError);
